import { useMemo, useState } from 'react';
import { CaretDown, CaretUp } from '@phosphor-icons/react';
import css from './PRTable.module.css';

interface PRRow {
  exercise_id: string;
  name: string | null;
  region: string | null;
  total_sets: number;
  heaviest_kg: number | null;
  heaviest_reps: number | null;
  heaviest_date: string | null;
  best_1rm: number | null;
  best_1rm_kg: number | null;
  best_1rm_reps: number | null;
  best_1rm_date: string | null;
}

type SortKey = 'best_1rm' | 'heaviest_kg' | 'total_sets' | 'name';

interface Props {
  data: PRRow[];
  onSelect?: (exerciseId: string) => void;
}

export function PRTable({ data, onSelect }: Props) {
  const [sortKey, setSortKey] = useState<SortKey>('best_1rm');
  const [sortDesc, setSortDesc] = useState(true);

  const sorted = useMemo(() => {
    const out = [...data];
    out.sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === 'string' && typeof bv === 'string') {
        return sortDesc ? bv.localeCompare(av) : av.localeCompare(bv);
      }
      return sortDesc ? Number(bv) - Number(av) : Number(av) - Number(bv);
    });
    return out;
  }, [data, sortKey, sortDesc]);

  function toggle(key: SortKey) {
    if (sortKey === key) {
      setSortDesc((v) => !v);
    } else {
      setSortKey(key);
      setSortDesc(true);
    }
  }

  function Arrow({ k }: { k: SortKey }) {
    if (sortKey !== k) return null;
    return sortDesc
      ? <CaretDown size={11} weight="bold" className={css.sortIcon} />
      : <CaretUp size={11} weight="bold" className={css.sortIcon} />;
  }

  if (sorted.length === 0) {
    return <div className={css.empty}>No PRs yet.</div>;
  }

  return (
    <div className={css.wrap}>
      <table className={css.table}>
        <thead>
          <tr>
            <th className={css.thLeft}>
              <button type="button" className={css.sortBtn} onClick={() => toggle('name')}>
                Exercise<Arrow k="name" />
              </button>
            </th>
            <th className={css.thRight}>
              <button type="button" className={css.sortBtn} onClick={() => toggle('best_1rm')}>
                Est 1RM<Arrow k="best_1rm" />
              </button>
            </th>
            <th className={css.thRight}>
              <button type="button" className={css.sortBtn} onClick={() => toggle('heaviest_kg')}>
                Heaviest set<Arrow k="heaviest_kg" />
              </button>
            </th>
            <th className={css.thRight}>
              <button type="button" className={css.sortBtn} onClick={() => toggle('total_sets')}>
                Sets<Arrow k="total_sets" />
              </button>
            </th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((r) => (
            <tr
              key={r.exercise_id}
              className={onSelect ? css.rowClickable : ''}
              onClick={() => onSelect?.(r.exercise_id)}
            >
              <td className={css.nameCell}>
                <div className={css.exName}>{r.name ?? r.exercise_id}</div>
                {r.region && <div className={css.region}>{r.region}</div>}
              </td>
              <td className={css.num}>
                {r.best_1rm != null ? (
                  <>
                    <span className={css.primary}>{r.best_1rm} kg</span>
                    {r.best_1rm_kg != null && r.best_1rm_reps != null && r.best_1rm_date && (
                      <div className={css.secondary}>
                        {r.best_1rm_kg}kg × {r.best_1rm_reps} · {r.best_1rm_date}
                      </div>
                    )}
                  </>
                ) : (
                  '—'
                )}
              </td>
              <td className={css.num}>
                {r.heaviest_kg != null && r.heaviest_reps != null ? (
                  <>
                    <span className={css.primary}>
                      {r.heaviest_kg}kg × {r.heaviest_reps}
                    </span>
                    {r.heaviest_date && (
                      <div className={css.secondary}>{r.heaviest_date}</div>
                    )}
                  </>
                ) : (
                  '—'
                )}
              </td>
              <td className={css.num}>{r.total_sets.toLocaleString()}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
